import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "react-router-dom";
import LoadingPlaceholder from "../ui/loading/LoadingPlaceholder";
import { CREATE_USER_SCHEMA } from "../../utils/schemas/accounts/createUserSchema";
import { generateGenericFormState } from "../../utils/generators/states/forms/genericFormState";
import { createFormSchema } from "../../utils/validation/generalValidation";
import { renderField as dynamicRenderField } from "../../utils/forms/renderers/DynamicFieldRenderer";
import SmallAlert from "../ui/alerts/SmallAlert";
import VerificationModal from "../ui/popup/VerificationModal";
import {
  getReferralCookie,
  deleteReferralCookie,
  deleteAccountCreationCookie,
  getAccountCreationCookie,
} from "../../utils/cookies/cookieHelpers";
import { store } from "../../store";
import { loginRdx } from "../../store/redux_auth/authSlice";
import { trackSignUp } from "../tracking/utils/events";
import SocialAuth from "../../pages/publics/utils/socialauth/SocialAuth";
import TermsConsentNotice from "../ui/auth/TermsConsentNotice";
import { associateAuthenticatedInstallationIfNative } from "../../utils/native/installationAssociation";

function CreateUserForm() {
  const navigate = useNavigate();
  const [currentErrors, setCurrentErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupStep, setPopupStep] = useState("loading"); // "loading", "verification", or "success"
  const [userEmail, setUserEmail] = useState("");
  const [storedFormData, setStoredFormData] = useState(null); // Store form data after first call

  const { User_Info = [] } = CREATE_USER_SCHEMA || {};
  const schemaSections = User_Info;

  // Generate schema dynamically from CREATE_USER_SCHEMA
  const { completeSchema: userFormSchema } =
    createFormSchema(CREATE_USER_SCHEMA);

  // Initialize react-hook-form
  const formMethods = useForm({
    resolver: zodResolver(userFormSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const [initialFormState, setInitialFormState] = useState(null);

  useEffect(() => {
    const generated = generateGenericFormState({ schemaSections });
    setInitialFormState(generated);

    // Reset form with initial values when they're ready
    if (generated && Object.keys(generated).length > 0) {
      formMethods.reset(generated);
    }
  }, [schemaSections, formMethods]);

  const focusFieldByName = (name) => {
    requestAnimationFrame(() => {
      const el = document.querySelector(`input[name="${name}"]`);
      if (el) {
        el.focus();
        if (typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    });
  };

  const handleSubmit = async (data) => {
    // Clear current errors on new submission
    setCurrentErrors([]);
    setIsSubmitting(true);
    setIsPopupOpen(true);
    setPopupStep("loading");
    setUserEmail(data.email || "");

    // Store form data in state for security (won't send again until verification)
    const formDataToStore = {
      firstname: data.firstname || "",
      email: data.email || "",
      password: data.password || "",
      confirm_password: data.confirm_password || "",
    };
    setStoredFormData(formDataToStore);

    try {
      // First call: verify account - check if account can be created
      const endpointPath = "account/verify-account";
      const apiBaseUrl = process.env.REACT_APP_API_DOMAIN;
      const urlPath = apiBaseUrl.concat("/", endpointPath);
      const response = await fetch(urlPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: data.email || "",
        }),
      });

      if (response.status === 400) {
        // Handle 400 error - show error message
        const errorData = await response.json();
        const errorMessage =
          errorData.message || "There was an error verifying your account.";

        setIsPopupOpen(false);
        setIsSubmitting(false);
        setCurrentErrors([errorMessage]);

        // Focus email field if it's an email-related error
        if (/email/i.test(errorMessage)) {
          setTimeout(() => {
            focusFieldByName("email");
          }, 200);
        }
        return;
      }

      if (response.status === 200) {
        // Success - show verification code input
        setPopupStep("verification");
        setIsSubmitting(false);
        return;
      }

      // Handle other errors
      setIsPopupOpen(false);
      setIsSubmitting(false);
      setCurrentErrors(["An unexpected error occurred. Please try again."]);
    } catch (err) {
      setIsPopupOpen(false);
      setIsSubmitting(false);
      setCurrentErrors(["Network error. Please try again."]);
    }
  };

  const handleVerifyCode = async (code) => {
    if (!code || code.length !== 6) {
      setCurrentErrors(["Please enter the complete 6-digit code."]);
      return;
    }

    if (!storedFormData) {
      setCurrentErrors(["Form data is missing. Please try again."]);
      return;
    }

    setIsSubmitting(true);
    setCurrentErrors([]);

    try {
      // Check for referral code in cookie
      const referralCode = getReferralCookie();

      // Second call: create account with verification code
      const endpointPath = "account/create-user";
      const apiBaseUrl = process.env.REACT_APP_API_DOMAIN;
      const urlPath = apiBaseUrl.concat("/", endpointPath);

      const requestBody = {
        first_name: storedFormData.firstname,
        email: storedFormData.email,
        password: storedFormData.password,
        confirm_password: storedFormData.confirm_password,
        termsConditions: true,
        verification_code: code,
      };

      const accountCreationRef = getAccountCreationCookie();

      if (accountCreationRef) {
        requestBody.token_ref = accountCreationRef;
      }

      // Include referral code if available
      if (referralCode) {
        requestBody.referral_code = referralCode;
      }

      const response = await fetch(urlPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(requestBody),
      });

      if (response.status === 201 || response.ok) {
        // Parse response to get user data
        const resData = await response.json();
        // Track conversion (Meta + LinkedIn)
        trackSignUp();
        const role = resData.user_role;
        const name = resData.user_name;
        const preferences = resData.preferences;
        const account_status = resData.account_status;
        const connected_account = resData.stripe_connected_account;
        const affiliates_enabled = resData.affiliates_enabled;
        const financial_insights = resData.financial_insights;

        // Prepare user data for Redux
        const userData = {
          user_name: name,
          user_role: role,
          has_usable_password: true,
          preferences: preferences,
          account_status: account_status,
          stripe_connected_account: connected_account,
          affiliates_enabled: affiliates_enabled || false,
          financial_insights: financial_insights,
          ista: resData.ista || false,
        };

        // Dispatch Redux action to update state with user data
        // Knox HTTP-only cookie is the source of truth for authentication
        store.dispatch(loginRdx(userData));

        // Fire-and-forget; must never delay or block signup.
        void associateAuthenticatedInstallationIfNative();

        // Delete referral cookie after successful signup
        if (referralCode) {
          deleteReferralCookie();
        }

        // Delete account creation referral cookie
        if (accountCreationRef) {
          deleteAccountCreationCookie();
        }

        // Show success message, then will auto-redirect via onSuccess
        setPopupStep("success");
        setIsSubmitting(false);
      } else {
        // Handle errors
        let errorMessage = "Failed to create account. Please try again.";
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          console.log("Could not parse error response");
        }
        setCurrentErrors([errorMessage]);
        setIsSubmitting(false);
      }
    } catch (err) {
      setCurrentErrors(["Network error. Please try again."]);
      setIsSubmitting(false);
    }
  };

  const handleGoToApp = () => {
    navigate("/");
  };

  const handleDismissError = () => {
    setCurrentErrors([]);
  };

  const isReady =
    initialFormState !== null &&
    Array.isArray(schemaSections) &&
    schemaSections.length > 0;

  if (!isReady) {
    return <LoadingPlaceholder rows={1} type="card-large" />;
  }

  const {
    register,
    handleSubmit: handleRHFSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    trigger,
    getValues,
  } = formMethods;

  return (
    <>
      <div className="auth-form">
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleRHFSubmit(handleSubmit)(e);
          }}
        >
          <div className="auth-form-content">
            {/* Show backend errors in SmallAlert */}
            {currentErrors.length > 0 && (
              <>
                <SmallAlert
                  key={currentErrors.join(",")}
                  alert="True"
                  type="error"
                  message={currentErrors[0]}
                  dynamicSize
                  dismissOnClickOutside={true}
                  dismissOnFocusOutside={false}
                  onDismiss={handleDismissError}
                />
              </>
            )}

            {/* Section 1: Title */}
            <p className="auth-form-title">
              Create your Plaintr account or <Link to="/sign-in">Sign in</Link>{" "}
              if you already have one.
            </p>

            {/* Section 2: Form Fields */}
            <div className="auth-form-fields">
              {/* Render fields using DynamicFieldRenderer */}
              {schemaSections[0].fields.map((field) => (
                <div key={field.name}>
                  {dynamicRenderField({
                    field,
                    formState: {},
                    setFormState: () => {},
                    handleToggleComposedField: () => {},
                    handleValueChange: (fieldName, value) =>
                      setValue(fieldName, value),
                    formMethods: {
                      register,
                      setValue,
                      getValues,
                      formState: { errors },
                      trigger,
                      watch,
                    },
                  })}
                </div>
              ))}
              <button
                type="submit"
                className="cta-button primary"
                disabled={!isValid || isSubmitting}
              >
                Create Account
              </button>
            </div>
          </div>
        </Form>

        {/* Verification Modal */}
        <VerificationModal
          isOpen={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          onConfirm={handleVerifyCode}
          onSuccess={handleGoToApp}
          email={userEmail}
          step={popupStep}
          isLoading={isSubmitting}
          errors={currentErrors}
          onDismissError={handleDismissError}
          minWidth={300}
          maxWidth={400}
        />
      </div>
    </>
  );
}

export default CreateUserForm;
